import { useMemo } from "react";
import {
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Theme,
  useMediaQuery,
  Container,
} from "@mui/material";
import { Button, Modal } from "@covertree/ui";
import { Offer } from "@covertree/new-business-graphql";
import {
  getCoveragesData,
  CoveragesFlowData,
  PersonalizedPlanDetailsType,
  PersonalizedPlanDetailsDescription,
} from "@covertree/new-business-forms";

import { CompareOfferTableHeaderCell, CompareOfferTableRow } from "./Table";
import { getKeySuffix, slugify } from "@covertree/utils";
import {
  MobileDetailsModalBody,
  MobileDetailsModalFooter,
  MobileDetailsModalHeader,
} from "./Mobile";

interface Props {
  open: boolean;
  personalizedPlans: PersonalizedPlanDetailsType[];
  coveragesFlowData: CoveragesFlowData;
  onClose: () => void;
  onPlanSelect: (offer: Offer) => void;
}

export function PersonalizedQuotesComparePlansModal({
  open,
  personalizedPlans,
  coveragesFlowData,
  onClose,
  onPlanSelect,
}: Props) {
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"));

  const coveragesData = useMemo(
    () => getCoveragesData(coveragesFlowData),
    [coveragesFlowData]
  );

  return (
    <Modal
      title="Comparing plans"
      open={open}
      onClose={onClose}
      maxWidth={isDesktop ? "md" : "sm"}
      sx={{
        "& .MuiPaper-root > .MuiBox-root:nth-of-type(2)": {
          padding: (theme) => theme.spacing(3, 3, 0),
        },
      }}>
      {isDesktop ? (
        <Table
          sx={(theme) => ({
            marginLeft: theme.spacing(-1.5),
            marginRight: theme.spacing(-1.5),
          })}>
          <TableHead>
            <TableRow>
              <TableCell width={personalizedPlans.length > 1 ? "45%" : "70%"} />
              {personalizedPlans.map(
                ({ title, monthlyPrice, yearlyPrice, name, isRecommended }) => (
                  <CompareOfferTableHeaderCell
                    key={name}
                    title={title}
                    dataTestId={`${name}-plan-test`}
                    monthlyPrice={monthlyPrice}
                    yearlyPrice={yearlyPrice}
                    isRecommended={isRecommended}
                  />
                )
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {coveragesData.map((coverage) => (
              <CompareOfferTableRow
                key={getKeySuffix(coverage.getName(), "card")}
                rowHeader={coverage}
                rowData={personalizedPlans.map(
                  (personalizedPlan) =>
                    personalizedPlan[
                      coverage.accessor
                    ] as PersonalizedPlanDetailsDescription
                )}
              />
            ))}
            <CompareOfferTableRow
              componentData={personalizedPlans.map(
                ({ name, title, isRecommended, offer }) => (
                  <Button
                    key={getKeySuffix(name, "btn")}
                    data-testid={`${slugify(title)}-select-button`}
                    size="small"
                    color="primary"
                    variant={isRecommended ? "contained" : "outlined"}
                    onClick={() => {
                      onPlanSelect(offer);
                      onClose();
                    }}
                    fullWidth>
                    Select
                  </Button>
                )
              )}
            />
          </TableBody>
        </Table>
      ) : (
        <Container maxWidth="sm" sx={{ padding: 0 }} disableGutters>
          <MobileDetailsModalHeader personalizedPlans={personalizedPlans} />
          <MobileDetailsModalBody
            personalizedPlans={personalizedPlans}
            coveragesData={coveragesData}
          />
          <MobileDetailsModalFooter
            personalizedPlans={personalizedPlans}
            onPlanSelect={onPlanSelect}
            onClose={onClose}
          />
        </Container>
      )}
    </Modal>
  );
}
